import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import Cookies from 'js-cookie';

import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';

import { CURRENCY_OPTIONS } from '../../constants';
import { displayTime, pluralize } from '../../utils/CommonUtils';
import { useShift } from '../../context/ShiftProvider';
import useAuthenticatedRequest from '../../utils/TokenRefreshUtils';
import useValidateShift from '../../utils/ShiftUtils';

import Card from '../common/Card';
import Drawer from '../common/Drawer';
import EmptyState from '../common/EmptyState';
import Money from '../common/Money';
import SummaryList, { SummaryRow } from '../common/SummaryList';

const EMPTY_SHIFT_DATA = {
    shift_data: null,
    current_balance: [],
    sales: null,
    cash_deposits: null,
    cash_withdraws: null,
    revenue: [],
};

/**
 * Головний екран касира: стан зміни, залишок у касі, рух коштів.
 *
 * Було: чотири однакові Accordion-и один під одним і три однокольорові
 * кнопки, розтягнуті на 8 з 12 колонок навіть на телефоні.
 */
const Shift = () => {
    const { isShiftOpen, openShift, closeShift } = useShift();
    const authenticatedRequest = useAuthenticatedRequest();
    const validateShift = useValidateShift();

    const toast = useRef(null);
    const location = useLocation();

    const [shiftData, setShiftData] = useState(EMPTY_SHIFT_DATA);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [cashDrawer, setCashDrawer] = useState(null); // 'deposit' | 'withdraw' | null
    const [reloadToken, setReloadToken] = useState(0);

    const reload = useCallback(() => setReloadToken((value) => value + 1), []);

    // Повідомлення, передане іншим екраном через navigate(..., { state })
    useEffect(() => {
        if (location.state?.toast) {
            toast.current?.show(location.state.toast);
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);

            const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/get_current_shift_data/`;
            const options = { method: 'GET', headers: { 'Content-Type': 'application/json' } };

            try {
                const response = await authenticatedRequest(url, options);

                if (!response || response.status !== 200) {
                    throw new Error(`HTTP ${response?.status}`);
                }

                const parsed = await response.json();

                if (cancelled) {
                    return;
                }

                setShiftData({ ...EMPTY_SHIFT_DATA, ...parsed });

                if (parsed.shift_data) {
                    openShift(parsed.shift_data.id);
                } else {
                    closeShift();
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Не вдалося отримати дані зміни', error);
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Дані зміни недоступні',
                        detail: 'Перевірте зв’язок із сервером',
                        life: 4000,
                    });
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [authenticatedRequest, reloadToken, openShift, closeShift]);

    const balance = useMemo(() => shiftData.current_balance ?? [], [shiftData]);

    /** Скільки готівки кожної валюти можна вилучити просто зараз. */
    const withdrawLimits = useMemo(
        () =>
            balance.reduce((acc, item) => {
                acc[item.currency] = Number(item.sum) || 0;
                return acc;
            }, {}),
        [balance]
    );

    // ── Внесення готівки ────────────────────────────────────────────────
    const depositFormik = useFormik({
        initialValues: { rows: [{ currency: 'UAH', sum: 0 }] },
        onSubmit: async ({ rows }) => {
            const sums = rows
                .filter((row) => Number(row.sum) > 0)
                .map(({ currency, sum }) => ({ currency, sum: Number(sum) }));

            if (!sums.length) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Порожня сума',
                    detail: 'Вкажіть суму більшу за нуль',
                    life: 3000,
                });
                return;
            }

            await submitCashMovement(sums, 'CASH_DEPOSIT');
            depositFormik.resetForm();
        },
    });

    // ── Вилучення готівки ───────────────────────────────────────────────
    const withdrawFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            rows: balance.map((item) => ({ currency: item.currency, sum: 0 })),
        },
        validate: ({ rows }) => {
            const rowErrors = {};

            rows.forEach((row, index) => {
                const limit = withdrawLimits[row.currency] ?? 0;
                const value = Number(row.sum);

                if (Number.isNaN(value) || value < 0) {
                    rowErrors[index] = 'Сума має бути додатнім числом';
                } else if (value > limit) {
                    rowErrors[index] = `У касі лише ${limit.toFixed(2)} ${row.currency}`;
                }
            });

            return Object.keys(rowErrors).length ? { rows: rowErrors } : {};
        },
        onSubmit: async ({ rows }) => {
            const sums = rows
                .filter((row) => Number(row.sum) > 0)
                .map(({ currency, sum }) => ({ currency, sum: Number(sum) }));

            if (!sums.length) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Порожня сума',
                    detail: 'Вкажіть суму більшу за нуль',
                    life: 3000,
                });
                return;
            }

            await submitCashMovement(sums, 'CASH_WITHDRAW');
            withdrawFormik.resetForm();
        },
    });

    const submitCashMovement = async (sums, type) => {
        setBusy(true);

        try {
            if (!(await validateShift())) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Зміна закрита',
                    detail: 'Операція неможлива — відкрийте зміну',
                    life: 4000,
                });
                return;
            }

            const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/create_cash_deposit_or_withdraw/`;
            const options = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    shift_id: Cookies.get('ShiftId'),
                    sums,
                    type,
                }),
            };

            const response = await authenticatedRequest(url, options);
            const isDeposit = type === 'CASH_DEPOSIT';

            if (response?.status === 201) {
                toast.current?.show({
                    severity: 'success',
                    summary: isDeposit ? 'Готівку внесено' : 'Готівку вилучено',
                    life: 3000,
                });
                setCashDrawer(null);
                reload();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: isDeposit ? 'Не вдалося внести готівку' : 'Не вдалося вилучити готівку',
                    detail: `Код помилки: ${response?.status ?? 'невідомо'}`,
                    life: 4000,
                });
            }
        } finally {
            setBusy(false);
        }
    };

    // ── Відкриття / закриття зміни ──────────────────────────────────────
    const changeShiftStatus = async () => {
        setBusy(true);

        try {
            const action = isShiftOpen ? 'close_shift' : 'open_shift';
            const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/${action}/`;
            const options = { method: 'GET', headers: { 'Content-Type': 'application/json' } };

            const response = await authenticatedRequest(url, options);

            if (response?.status === 201) {
                const parsed = await response.json();

                if (isShiftOpen) {
                    closeShift();
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Зміну закрито',
                        life: 3000,
                    });
                } else {
                    openShift(parsed.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Зміну відкрито',
                        life: 3000,
                    });
                }

                reload();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: isShiftOpen ? 'Не вдалося закрити зміну' : 'Не вдалося відкрити зміну',
                    detail: `Код помилки: ${response?.status ?? 'невідомо'}`,
                    life: 4000,
                });
            }
        } finally {
            setBusy(false);
        }
    };

    const confirmShiftChange = () => {
        confirmDialog({
            header: isShiftOpen ? 'Закрити зміну' : 'Відкрити зміну',
            message: isShiftOpen
                ? 'Після закриття зміни продажі стануть недоступними до відкриття нової.'
                : 'Відкрити нову касову зміну?',
            icon: isShiftOpen ? 'pi pi-lock' : 'pi pi-unlock',
            acceptLabel: isShiftOpen ? 'Закрити' : 'Відкрити',
            rejectLabel: 'Скасувати',
            acceptClassName: isShiftOpen ? 'p-button-danger' : '',
            accept: changeShiftStatus,
        });
    };

    // ── Рендер ──────────────────────────────────────────────────────────
    const movementSections = [
        { key: 'sales', title: 'Продажі', icon: 'pi pi-shopping-cart', data: shiftData.sales },
        {
            key: 'deposits',
            title: 'Внесення',
            icon: 'pi pi-arrow-down-left',
            data: shiftData.cash_deposits,
            tone: 'income',
        },
        {
            key: 'withdraws',
            title: 'Виплати',
            icon: 'pi pi-arrow-up-right',
            data: shiftData.cash_withdraws,
            tone: 'outcome',
        },
    ].filter((section) => section.data?.amount?.length);

    const revenue = shiftData.revenue ?? [];

    const cashRows = (formik, options = {}) =>
        formik.values.rows.map((row, index) => {
            const limit = withdrawLimits[row.currency];
            const error = formik.errors.rows?.[index];

            return (
                // eslint-disable-next-line react/no-array-index-key
                <div className="rc-payment-row" key={`row-${index}`}>
                    <div className="rc-payment-row__top">
                        <div className="rc-payment-row__currency">
                            <Dropdown
                                value={row.currency}
                                options={CURRENCY_OPTIONS}
                                disabled={options.lockCurrency}
                                onChange={(e) =>
                                    formik.setFieldValue(`rows[${index}].currency`, e.value)
                                }
                                aria-label="Валюта"
                            />
                        </div>
                        <div className="rc-payment-row__amount">
                            <InputNumber
                                value={row.sum}
                                onValueChange={(e) =>
                                    formik.setFieldValue(`rows[${index}].sum`, e.value ?? 0)
                                }
                                mode="decimal"
                                locale="uk-UA"
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                inputMode="decimal"
                                className={error ? 'p-invalid' : ''}
                                aria-label="Сума"
                            />
                        </div>
                        {options.lockCurrency ? (
                            <Button
                                type="button"
                                icon={row.sum === limit ? 'pi pi-times' : 'pi pi-angle-double-up'}
                                severity="secondary"
                                text
                                rounded
                                aria-label={row.sum === limit ? 'Очистити' : 'Вся сума'}
                                onClick={() =>
                                    formik.setFieldValue(
                                        `rows[${index}].sum`,
                                        row.sum === limit ? 0 : limit
                                    )
                                }
                            />
                        ) : (
                            <Button
                                type="button"
                                icon="pi pi-trash"
                                severity="danger"
                                text
                                rounded
                                disabled={formik.values.rows.length === 1}
                                aria-label="Видалити рядок"
                                onClick={() =>
                                    formik.setFieldValue(
                                        'rows',
                                        formik.values.rows.filter((_, i) => i !== index)
                                    )
                                }
                            />
                        )}
                    </div>
                    {error ? (
                        <small className="rc-field-error">{error}</small>
                    ) : (
                        options.lockCurrency && (
                            <small className="rc-field-hint">
                                Доступно: {limit?.toFixed(2)} {row.currency}
                            </small>
                        )
                    )}
                </div>
            );
        });

    return (
        <>
            <Toast ref={toast} position="top-center" />

            <div className="rc-stack">
                <div>
                    <h1 className="rc-page-title">Каса</h1>
                    <p className="rc-page-subtitle">
                        {isShiftOpen && shiftData.shift_data
                            ? `Зміна №${shiftData.shift_data.id} · відкрита о ${displayTime(
                                  shiftData.shift_data.open_time
                              )}`
                            : 'Відкритої зміни немає'}
                    </p>
                </div>

                <div className="rc-action-grid">
                    <button
                        type="button"
                        className={`rc-action ${isShiftOpen ? 'rc-action--neutral' : 'rc-action--primary'}`}
                        onClick={confirmShiftChange}
                        disabled={busy}
                    >
                        <span className="rc-action__icon">
                            <i className={isShiftOpen ? 'pi pi-lock' : 'pi pi-power-off'} />
                        </span>
                        <span className="rc-action__text">
                            <span className="rc-action__title">
                                {isShiftOpen ? 'Закрити зміну' : 'Відкрити зміну'}
                            </span>
                            <span className="rc-action__hint">
                                {isShiftOpen
                                    ? 'Підсумки зміни підуть у звіт'
                                    : 'Без відкритої зміни продаж недоступний'}
                            </span>
                        </span>
                        <i className="pi pi-chevron-right rc-action__chevron" />
                    </button>
                </div>

                {isShiftOpen && (
                    <div className="rc-action-grid rc-action-grid--split">
                        <button
                            type="button"
                            className="rc-action rc-action--income"
                            onClick={() => setCashDrawer('deposit')}
                        >
                            <span className="rc-action__icon">
                                <i className="pi pi-arrow-down-left" />
                            </span>
                            <span className="rc-action__text">
                                <span className="rc-action__title">Внести гроші</span>
                                <span className="rc-action__hint">Поповнити касу</span>
                            </span>
                        </button>

                        <button
                            type="button"
                            className="rc-action rc-action--outcome"
                            onClick={() => setCashDrawer('withdraw')}
                            disabled={!balance.length}
                        >
                            <span className="rc-action__icon">
                                <i className="pi pi-arrow-up-right" />
                            </span>
                            <span className="rc-action__text">
                                <span className="rc-action__title">Забрати гроші</span>
                                <span className="rc-action__hint">Інкасація з каси</span>
                            </span>
                        </button>
                    </div>
                )}

                <Card title="Гроші в касі" flush>
                    {loading ? (
                        <EmptyState icon="pi pi-spin pi-spinner" text="Завантаження…" />
                    ) : balance.length ? (
                        <SummaryList>
                            {balance.map((item) => (
                                <SummaryRow key={item.currency} label={item.currency}>
                                    <Money value={item.sum} currency={item.currency} />
                                </SummaryRow>
                            ))}
                        </SummaryList>
                    ) : (
                        <EmptyState
                            icon="pi pi-wallet"
                            title="Каса порожня"
                            text="Внесіть готівку, щоб почати роботу зі здачею"
                        />
                    )}
                </Card>

                {isShiftOpen && movementSections.length > 0 && (
                    <Card title="Рух коштів за зміну" flush>
                        <SummaryList>
                            {movementSections.map((section) => (
                                <SummaryRow
                                    key={section.key}
                                    label={section.title}
                                    sublabel={
                                        section.data.quantity !== undefined
                                            ? pluralize(section.data.quantity, [
                                                  'операція',
                                                  'операції',
                                                  'операцій',
                                              ])
                                            : undefined
                                    }
                                >
                                    <span className="rc-summary-row__value--stack">
                                        {section.data.amount.map((amount) => (
                                            <Money
                                                key={`${section.key}-${amount.currency}`}
                                                value={amount.sum}
                                                currency={amount.currency}
                                                tone={section.tone}
                                            />
                                        ))}
                                    </span>
                                </SummaryRow>
                            ))}
                            {revenue.length > 0 && (
                                <SummaryRow label="Виручка" total>
                                    <span className="rc-summary-row__value--stack">
                                        {revenue.map((amount) => (
                                            <Money
                                                key={`revenue-${amount.currency}`}
                                                value={amount.sum}
                                                currency={amount.currency}
                                            />
                                        ))}
                                    </span>
                                </SummaryRow>
                            )}
                        </SummaryList>
                    </Card>
                )}
            </div>

            {/* Внесення готівки */}
            <Drawer
                visible={cashDrawer === 'deposit'}
                onHide={() => setCashDrawer(null)}
                title="Внести готівку"
                footer={
                    <>
                        <Button
                            label="Внести готівку"
                            icon="pi pi-check"
                            className="w-full"
                            loading={busy}
                            onClick={depositFormik.handleSubmit}
                        />
                        <Button
                            label="Скасувати"
                            severity="secondary"
                            text
                            className="w-full"
                            onClick={() => setCashDrawer(null)}
                        />
                    </>
                }
            >
                <form onSubmit={depositFormik.handleSubmit}>
                    {cashRows(depositFormik)}
                    <Button
                        type="button"
                        label="Додати валюту"
                        icon="pi pi-plus"
                        severity="secondary"
                        outlined
                        className="w-full mt-3"
                        onClick={() =>
                            depositFormik.setFieldValue('rows', [
                                ...depositFormik.values.rows,
                                { currency: 'UAH', sum: 0 },
                            ])
                        }
                    />
                </form>
            </Drawer>

            {/* Вилучення готівки */}
            <Drawer
                visible={cashDrawer === 'withdraw'}
                onHide={() => setCashDrawer(null)}
                title="Забрати готівку"
                footer={
                    <>
                        <Button
                            label="Забрати готівку"
                            icon="pi pi-check"
                            severity="danger"
                            className="w-full"
                            loading={busy}
                            disabled={!withdrawFormik.isValid}
                            onClick={withdrawFormik.handleSubmit}
                        />
                        <Button
                            label="Скасувати"
                            severity="secondary"
                            text
                            className="w-full"
                            onClick={() => setCashDrawer(null)}
                        />
                    </>
                }
            >
                <form onSubmit={withdrawFormik.handleSubmit}>
                    {cashRows(withdrawFormik, { lockCurrency: true })}
                </form>
            </Drawer>
        </>
    );
};

export default Shift;
